import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { searchService } from '../../services/searchService';

const SearchIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>;
const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>;
const Spinner = () => <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>;

export default function GlobalSearch({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Auto-focus with a slight delay to ensure render
      setTimeout(() => inputRef?.current?.focus(), 50);
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  // Debounced search logic
  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query?.length >= 2) {
        setLoading(true);
        try {
          const data = await searchService?.globalSearch(query);
          // Map searchService response to expected format
          setResults({
            events: data?.events || [],
            spots: data?.spots || [],
            products: data?.products || [],
            listings: data?.marketplace || [],
            totalCount: searchService?.getTotalResultsCount(data)
          });
        } catch (error) {
          console.error("Search failed", error);
        } finally {
          setLoading(false);
        }
      } else {
        setResults(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [query]);

  const handleNavigate = (path) => {
    navigate(path);
    onClose();
    setQuery('');
  };

  if (!isOpen) return null;

  // USE PORTAL TO RENDER OUTSIDE THE HEADER
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-20 px-4">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] animate-in fade-in zoom-in-95 duration-200">
        
        {/* Search Input Area */}
        <div className="flex items-center p-4 border-b border-gray-100 gap-3 bg-white">
          <div className="text-gray-400">
            {loading ? <Spinner /> : <SearchIcon />}
          </div>
          <input
            ref={inputRef}
            type="text"
            className="flex-1 text-lg outline-none text-gray-900 placeholder-gray-400 bg-transparent h-10"
            placeholder="Etkinlik, spot veya ürün ara..."
            value={query}
            onChange={(e) => setQuery(e?.target?.value)}
            onKeyDown={(e) => e?.key === 'Escape' && onClose()}
          />
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Results Area */}
        <div className="overflow-y-auto flex-1 bg-gray-50/50">
          {!query && (
            <div className="text-center py-12 text-gray-400">
              <p>Aramaya başlamak için en az 2 karakter yazın...</p>
            </div>
          )}

          {query?.length >= 2 && !loading && results?.totalCount === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>Sonuç bulunamadı.</p>
            </div>
          )}

          {results && (
            <div className="p-2 space-y-4">
              {results?.events?.length > 0 && (
                <Section title="Etkinlikler">
                  {results?.events?.map(item => (
                    <ResultItem 
                      key={item?.id} 
                      image={item?.image} 
                      title={item?.title} 
                      subtitle={item?.location} 
                      onClick={() => handleNavigate(`/event-details/${item?.id}`)} 
                      type="Etkinlik" 
                    />
                  ))}
                </Section>
              )}
              
              {results?.spots?.length > 0 && (
                <Section title="Spot Rehberi">
                  {results?.spots?.map(item => (
                    <ResultItem 
                      key={item?.id} 
                      image={item?.image} 
                      title={item?.name} 
                      subtitle={item?.location} 
                      onClick={() => handleNavigate(`/spot-details/${item?.id}`)} 
                      type="Spot" 
                    />
                  ))}
                </Section>
              )}

              {results?.products?.length > 0 && (
                <Section title="Mağaza">
                  {results?.products?.map(item => (
                    <ResultItem 
                      key={item?.id} 
                      image={item?.image} 
                      title={item?.title} 
                      subtitle={`${item?.price} ₺`} 
                      onClick={() => handleNavigate(`/official-store`)} 
                      type="Ürün" 
                    />
                  ))}
                </Section>
              )}

               {results?.listings?.length > 0 && (
                <Section title="İkinci El">
                  {results?.listings?.map(item => (
                    <ResultItem 
                      key={item?.id} 
                      image={item?.image} 
                      title={item?.title} 
                      subtitle={`${item?.price} ₺`} 
                      onClick={() => handleNavigate(`/user-marketplace`)} 
                      type="İlan" 
                    />
                  ))}
                </Section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body // PORTAL TARGET
  );
}

// Sub-components
const Section = ({ title, children }) => (
  <div className="mb-2">
    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-3 mt-2">{title}</h3>
    <div className="space-y-1">{children}</div>
  </div>
);

const ResultItem = ({ image, title, subtitle, onClick, type }) => (
  <div 
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-lg hover:bg-white hover:shadow-sm cursor-pointer group transition-all border border-transparent hover:border-gray-100"
  >
    <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-200 flex-shrink-0">
      {image ? <img src={image} className="w-full h-full object-cover" alt="" /> : <div className="w-full h-full bg-gray-300" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-gray-900 truncate">{title}</div>
      <div className="text-xs text-gray-500 truncate">{subtitle}</div>
    </div>
    <span className="text-[10px] text-gray-400 border px-1.5 py-0.5 rounded bg-gray-50">{type}</span>
  </div>
);